import { create } from "zustand";
import JSZip from "jszip";

const useTurnedOffLampsStore = create((set, get) => ({
  kmlObjects: [],
  modalTurnedOffLamps: false,

  setModalTurnedOffLamps: (value) =>
    set(() => ({ modalTurnedOffLamps: value })),

  setKmlObjects: (updater) => {
    set((state) => ({
      kmlObjects:
        typeof updater === "function" ? updater(state.kmlObjects) : updater,
    }));
  },

  addKmlObjects: (extractedKmlObjects) =>
    set((state) => ({
      kmlObjects: [...extractedKmlObjects],
    })),

  cleanKmlObjects: () =>
    set((state) => ({
      kmlObjects: [],
    })),

  handleKmzFile: async (kmzFile, filter = true) => {
    const unzipedKmz = await get().unzipKmz(kmzFile);
    try {
      if (unzipedKmz.length > 0) {
        const extractedKml = await get().extractKmlsFromKmz(unzipedKmz);

        if (extractedKml.length > 0) {
          const kmlObjects = await get().getObjectsFromKml(
            extractedKml,
            filter
          );

          return kmlObjects;
        }
      }
    } catch (error) {
      throw new Error("No objects found in kmz file!");
    }
  },

  unzipKmz: (file) => {
    return JSZip.loadAsync(file)
      .then((zip) => {
        return Object.entries(zip.files);
      })
      .catch((err) => {
        console.error(err);
        if (err.name === "AbortError") {
          console.log("Aborted");
        }
        return null;
      });
  },

  extractKmlsFromKmz: async (unzippedKmz) => {
    let auxKmlList = [];

    try {
      const contentKmls = unzippedKmz;

      for (let kmlPos = 0; kmlPos < contentKmls.length; kmlPos++) {
        const [filePath, fileEntry] = contentKmls[kmlPos];

        if (filePath.endsWith(".kml")) {
          const kmlFileContent = await fileEntry.async("text");
          const converted = kmlFileContent.replaceAll("ns2:", "");

          auxKmlList.push(converted);
        }
      }

      return auxKmlList;
    } catch (error) {
      console.error("Error extracting KMLs from KMZs", error);
    }
  },

  getObjectsFromKml: (kmlList, filter) => {
    try {
      for (let i = 0; i < kmlList.length; i++) {
        const kmlContent = kmlList[i];

        const tableMatches = kmlContent.match(/<table[\s\S]*?<\/table>/gi);

        if (tableMatches) {
          const keywords = [
            "quantidade lampadas",
            "quantidade_lampadas",
            "qtd lampadas",
            "qtd_lampadas",
            "qtdlamps",
            "num lampadas",
            "num_lampadas",
            "numero lampadas",
            "numero_lampadas",
            "numero de lampadas",
            "qtd_lamp",
            "qtd_lampad",
          ];

          const processedObjects = get()
            .processObjectData(tableMatches)
            .map((obj) => {
              for (const key of Object.keys(obj)) {
                const normalizedKey = key.toLowerCase().trim();
                if (keywords.includes(normalizedKey)) {
                  obj.qtd_lamp = obj[key];
                  break;
                }
              }
              return obj;
            });

          if (filter) {
            processedObjects
              .filter((o) => o.qtd_lamp > 0)
              .flatMap((o) =>
                Array.from({ length: o.qtd_lamp }, () => ({
                  ...o,
                  id: `${Date.now().toString(36)}-${Math.random()
                    .toString(36)
                    .substring(2, 10)}`,
                }))
              );
          }

          if (processedObjects) {
            return processedObjects;
          }
        } else {
          console.log("No <table> tags found in this KML.");
        }
      }

      throw new Error("No objects found!");
    } catch (e) {
      console.error(e);
      return [];
    }
  },

  processObjectData: (tables) => {
    const mapObjects = [];

    tables.forEach((tableHtml, index) => {
      const rows = tableHtml.match(/<tr[\s\S]*?<\/tr>/gi);
      const obj = {};

      if (rows) {
        rows.forEach((row) => {
          const tds = row.match(/<td[\s\S]*?<\/td>/gi);

          if (tds && tds.length === 2) {
            const key = tds[0]
              .replace(/<[^>]+>/g, "")
              .trim()
              .toLowerCase();
            let value = tds[1].replace(/<[^>]+>/g, "").trim();

            const convertedValue = value.replace(",", ".");

            obj[key] = isNaN(convertedValue) ? value : Number(convertedValue);
          }
        });

        if (Object.keys(obj).some((k) => k.includes("lamp"))) {
          obj.enabled = true;
          mapObjects.push(obj);
        }
      }
    });

    return mapObjects;
  },

  generateKmzFromPoints: (objects) => {
    const JSZip = require("jszip");
    const { create } = require("xmlbuilder2");

    const kmlDoc = create({ version: "1.0", encoding: "UTF-8" })
      .ele("kml", {
        xmlns: "http://www.opengis.net/kml/2.2",
        "xmlns:gx": "http://www.google.com/kml/ext/2.2",
        "xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
        "xsi:schemaLocation":
          "http://www.opengis.net/kml/2.2 " +
          "http://schemas.opengis.net/kml/2.2.0/ogckml22.xsd " +
          "http://www.google.com/kml/ext/2.2 " +
          "http://code.google.com/apis/kml/schema/kml22gx.xsd",
      })
      .ele("Document")
      .ele("name")
      .txt("Lamps")
      .up();

    const hexToKmlColor = (hex, alpha = "ff") => {
      const r = hex.slice(1, 3);
      const g = hex.slice(3, 5);
      const b = hex.slice(5, 7);
      return alpha + b + g + r;
    };

    const styles = {
      c2004a: "#c2004a",
      "00c278": "#00c278",
    };

    Object.entries(styles).forEach(([id, hex]) => {
      const kmlColor = hexToKmlColor(hex);
      kmlDoc
        .ele("Style", { id: `IconStyle-${id}` })
        .ele("IconStyle")
        .ele("color")
        .txt(kmlColor)
        .up()
        .ele("scale")
        .txt("1.2")
        .up()
        .up()
        .ele("LabelStyle")
        .ele("scale")
        .txt("0.8")
        .up()
        .up()
        .up();
    });

    // Add Placemarks
    objects.forEach((obj, idx) => {
      const id = obj.color.replace("#", "");
      const placemark = kmlDoc.ele("Placemark", {
        id: `ID_${idx.toString().padStart(5, "0")}`,
      });

      placemark
        .ele("name")
        .txt(obj.name || `Lamp-${idx}`)
        .up();

      const tableRows = Object.entries(obj)
        .map(([key, value]) => {
          let formattedValue = value;
          if (
            key.toLowerCase() === "latitude" ||
            key.toLowerCase() === "longitude"
          ) {
            formattedValue = String(value).replace(".", ",");
          }
          return `<tr><td>${key}</td><td>${formattedValue}</td></tr>`;
        })
        .join("\n");

      placemark
        .ele("description")
        .dat(
          `
      <html>
        <body>
          <table>
            ${tableRows}
          </table>
        </body>
      </html>
      `
        )
        .up();

      placemark.ele("styleUrl").txt(`#IconStyle-${id}`).up();

      placemark
        .ele("Point")
        .ele("coordinates")
        .txt(`${obj.longitude},${obj.latitude},0`)
        .up()
        .up();
    });

    const kmlString = kmlDoc.end({ prettyPrint: true });

    const zip = new JSZip();
    zip.file("doc.kml", kmlString);
    zip.generateAsync({ type: "blob" }).then((content) => {
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = "arquivo.kmz";
      a.click();
      URL.revokeObjectURL(url);
    });
  },
}));

export default useTurnedOffLampsStore;
